import React, { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { toast } from 'sonner'
import Button from '../../ui/Button'
import MultiSelect from '../../ui/MultiSelect'
import SingleSelect from '../../ui/SingleSelect'
import { useCatalog } from '../../../contexts/CatalogContext'
import { createTeamMember, updateTeamMember } from '../../../api/teamMembersApi'
import type { ProjectDetails } from '../../../types'
import type { TeamMember, TeamMemberRole, TeamMemberStatus } from '../../../types/teamMember'
import { PHONE_DIAL_CODES, buildInternationalPhone, parseStoredPhone } from '../../../constants/phoneDialCodes'
import { TEAM_MEMBER_EQUIPMENT_OPTIONS } from '../../../constants/teamMemberEquipment'
import { nextNumericMemberId } from '../../../utils/nextNumericMemberId'

const ROLE_OPTIONS: TeamMemberRole[] = ['admin', 'user', 'operator']
const STATUS_OPTIONS: TeamMemberStatus[] = ['active', 'inactive']

const DEFAULT_JOB_TITLES = [
  'Welding Engineer',
  'Inspector',
  'Quality Manager',
  'Welder',
  'Supervisor'
] as const

function roleLabel(role: TeamMemberRole): string {
  if (role === 'admin') return 'Admin'
  if (role === 'user') return 'User'
  return 'Operator'
}

function normalizeMemberRole(value: string): TeamMemberRole {
  if (value === 'admin' || value === 'user' || value === 'operator') return value
  if (value === 'admin_user') return 'admin'
  return 'operator'
}

function statusLabel(status: TeamMemberStatus): string {
  return status === 'active' ? 'Active' : 'Inactive'
}

/** Practical email check (not full RFC). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i

function isValidEmail(email: string): boolean {
  const t = email.trim()
  return t.length > 0 && EMAIL_RE.test(t)
}

interface TeamMemberFormModalProps {
  projectId: string
  project: ProjectDetails
  open: boolean
  onClose: () => void
  member: TeamMember | null
  /** Called after a successful create/update with the saved member. */
  onSaved: (member: TeamMember) => void
  /** Role used when opening create mode (defaults to operator). */
  defaultRole?: TeamMemberRole
  /** Existing external IDs used to suggest the next pure-integer Member ID. */
  existingMemberIds?: string[]
}

const emptyForm = {
  memberExternalId: '',
  name: '',
  email: '',
  jobTitle: '',
  status: 'active' as TeamMemberStatus,
  memberRole: 'operator' as TeamMemberRole,
  assignedEquipment: [] as string[]
}

const EMPTY_MEMBER_IDS: string[] = []

export default function TeamMemberFormModal({
  projectId,
  open,
  onClose,
  member,
  onSaved,
  defaultRole = 'operator',
  existingMemberIds = EMPTY_MEMBER_IDS
}: TeamMemberFormModalProps) {
  const { getLabelsMerged, addLabel } = useCatalog()
  const [form, setForm] = useState(emptyForm)
  const [phoneDial, setPhoneDial] = useState('+1')
  const [phoneNational, setPhoneNational] = useState('')
  const [saving, setSaving] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [memberIdError, setMemberIdError] = useState<string | null>(null)

  const equipmentOptions = useMemo(() => [...TEAM_MEMBER_EQUIPMENT_OPTIONS], [])
  const jobTitleOptions = useMemo(
    () => getLabelsMerged('team_member_job_title', [...DEFAULT_JOB_TITLES, form.jobTitle]),
    [getLabelsMerged, form.jobTitle]
  )

  const phoneDialOptions = useMemo(() => PHONE_DIAL_CODES.map((c) => c.dial), [])
  const phoneDialLabelByDial = useMemo(() => {
    const m: Record<string, string> = {}
    for (const { dial, label } of PHONE_DIAL_CODES) m[dial] = label
    return m
  }, [])

  const isEdit = Boolean(member)
  const existingIdsKey = existingMemberIds.join('\0')

  useEffect(() => {
    if (!open) return
    if (member) {
      const parsed = parseStoredPhone(member.phone ?? '')
      setForm({
        memberExternalId: member.memberExternalId ?? '',
        name: member.name,
        email: member.email,
        jobTitle: member.jobTitle ?? '',
        status: member.status,
        memberRole: normalizeMemberRole(String(member.memberRole)),
        assignedEquipment: [...(member.assignedEquipment ?? [])]
      })
      setPhoneDial(phoneDialOptions.includes(parsed.dial) ? parsed.dial : '+1')
      setPhoneNational(parsed.national)
    } else {
      setForm({
        ...emptyForm,
        memberRole: defaultRole,
        memberExternalId: nextNumericMemberId(existingMemberIds)
      })
      setPhoneDial('+1')
      setPhoneNational('')
    }
    setEmailError(null)
    setPhoneError(null)
    setMemberIdError(null)
    // existingIdsKey captures existingMemberIds for create prefill without array identity churn
  }, [open, member, phoneDialOptions, defaultRole, existingIdsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const memberId = form.memberExternalId.trim()
    if (!memberId) {
      setMemberIdError('Member ID is required')
      toast.error('Member ID is required')
      return
    }
    setMemberIdError(null)

    if (form.email.trim() && !isValidEmail(form.email)) {
      setEmailError('Enter a valid email address')
      toast.error('Enter a valid email address')
      return
    }
    setEmailError(null)

    const phoneCombined = buildInternationalPhone(phoneDial, phoneNational)
    if (phoneNational.replace(/\D/g, '').length > 0 && phoneCombined.length < 8) {
      setPhoneError('Enter a complete phone number')
      toast.error('Enter a complete phone number')
      return
    }
    setPhoneError(null)

    if (form.memberRole === 'operator' && form.assignedEquipment.length === 0) {
      toast.error('Select at least one equipment for Operator role')
      return
    }
    setSaving(true)
    try {
      const payload = {
        memberExternalId: memberId,
        memberRole: form.memberRole,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: phoneCombined,
        jobTitle: form.jobTitle.trim(),
        status: form.status,
        assignedEquipment: form.memberRole === 'operator' ? form.assignedEquipment : [],
        plateSources: [] as string[]
      }
      let saved: TeamMember
      if (isEdit && member) {
        saved = await updateTeamMember(projectId, member.id, payload)
        toast.success('Team member updated')
      } else {
        saved = await createTeamMember(projectId, payload)
        toast.success('Team member created')
      }
      onSaved(saved)
      onClose()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  const content = (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-gray-900/40 px-4 py-8"
      role="dialog"
      aria-modal="true"
    >
      {/*
        Flex-column shell: header + footer are `shrink-0` and the form body
        is the lone `flex-1 overflow-y-auto` region. This pins Cancel /
        Create to the bottom edge of the modal regardless of how much the
        form grows (e.g. switching role to Operator reveals the Equipment
        row), and also keeps them on-screen while a MultiSelect dropdown
        is open below them. Bumped `max-h` to 95vh to recover the vertical
        space the previous static `overflow-y-auto` shell wasted on the
        page-level padding.
      */}
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col rounded-lg border border-gray-200 bg-white shadow-lg">
        <div className="shrink-0 border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">{isEdit ? 'Edit team member' : 'Add team member'}</h2>
        </div>
        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-gray-600" htmlFor="tm-member-id">
                Member ID
              </label>
              <input
                id="tm-member-id"
                className={`input-primary mt-1 text-sm ${memberIdError ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                required
                value={form.memberExternalId}
                onChange={(e) => {
                  setForm((f) => ({ ...f, memberExternalId: e.target.value }))
                  if (memberIdError) setMemberIdError(null)
                }}
              />
              {memberIdError ? <p className="mt-1 text-xs text-error-600">{memberIdError}</p> : null}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Name</label>
              <input
                className="input-primary mt-1 text-sm"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600" htmlFor="tm-job-title">
                Job title
              </label>
              <SingleSelect
                className="mt-1"
                value={form.jobTitle}
                options={jobTitleOptions}
                onChange={(jobTitle) => setForm((f) => ({ ...f, jobTitle }))}
                placeholder="Select job title…"
                addLabel="Add Job Title"
                createModalTitle="Add Job Title"
                createFieldLabel="Job title"
                createInputPlaceholder="e.g. Welding Engineer"
                searchPlaceholder="Search job title…"
                onCreateOption={async (value) => {
                  await addLabel('team_member_job_title', value)
                  return value.trim()
                }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600" htmlFor="tm-email">
                Email
              </label>
              <input
                id="tm-email"
                type="email"
                autoComplete="email"
                className={`input-primary mt-1 text-sm ${emailError ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                value={form.email}
                onChange={(e) => {
                  setForm((f) => ({ ...f, email: e.target.value }))
                  if (emailError) setEmailError(null)
                }}
                onBlur={() => {
                  if (form.email.trim() && !isValidEmail(form.email)) {
                    setEmailError('Enter a valid email address')
                  } else {
                    setEmailError(null)
                  }
                }}
              />
              {emailError ? <p className="mt-1 text-xs text-error-600">{emailError}</p> : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-gray-600">Role</label>
              <MultiSelect
                className="mt-1"
                values={[form.memberRole]}
                options={[...ROLE_OPTIONS]}
                maxSelections={1}
                getOptionLabel={(v) => roleLabel(v as TeamMemberRole)}
                getTriggerLabel={(sel) => roleLabel(sel[0] as TeamMemberRole)}
                placeholder="Select role…"
                searchPlaceholder="Search role…"
                onChange={(sel) => {
                  const raw = sel[0] as TeamMemberRole | undefined
                  const next: TeamMemberRole =
                    raw === 'admin' || raw === 'user' || raw === 'operator' ? raw : 'operator'
                  setForm((f) => ({
                    ...f,
                    memberRole: next,
                    assignedEquipment: next === 'operator' ? f.assignedEquipment : []
                  }))
                }}
              />
            </div>
            <div>
              <span className="text-xs font-medium text-gray-600">Phone</span>
              <div className="mt-1 flex w-full items-stretch gap-2">
                <MultiSelect
                  className="w-[30%] min-w-0 shrink-0"
                  inputClassName="text-sm"
                  ariaLabel="Country calling code"
                  values={[phoneDial]}
                  options={phoneDialOptions}
                  maxSelections={1}
                  getOptionLabel={(d) => phoneDialLabelByDial[d] ?? d}
                  getTriggerLabel={(sel) => phoneDialLabelByDial[sel[0]] ?? sel[0] ?? '+1'}
                  placeholder="Select country code…"
                  searchPlaceholder="Search country code…"
                  onChange={(sel) => {
                    const raw = sel[0]
                    const next = raw && phoneDialOptions.includes(raw) ? raw : '+1'
                    setPhoneDial(next)
                    if (phoneError) setPhoneError(null)
                  }}
                />
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder="National number"
                  className={`input-primary min-w-0 flex-1 text-sm ${phoneError ? 'border-error-500 focus:border-error-500 focus:ring-error-500' : ''}`}
                  value={phoneNational}
                  onChange={(e) => {
                    setPhoneNational(e.target.value)
                    if (phoneError) setPhoneError(null)
                  }}
                  onBlur={() => {
                    const digits = phoneNational.replace(/\D/g, '')
                    if (digits.length > 0 && digits.length < 6) {
                      setPhoneError('Enter a complete phone number')
                    } else {
                      setPhoneError(null)
                    }
                  }}
                />
              </div>
              {phoneError ? <p className="mt-1 text-xs text-error-600">{phoneError}</p> : null}
              {/* <p className="mt-1 text-[11px] text-gray-500">Stored with country code (e.g. +971501234567).</p> */}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
            <label className="text-xs font-medium text-gray-600">Status</label>
            <MultiSelect
              className="mt-1"
              values={[form.status]}
              options={[...STATUS_OPTIONS]}
              maxSelections={1}
              getOptionLabel={(v) => statusLabel(v as TeamMemberStatus)}
              getTriggerLabel={(sel) => statusLabel(sel[0] as TeamMemberStatus)}
              placeholder="Select status…"
              searchPlaceholder="Search status…"
              onChange={(sel) => {
                const next = (sel[0] as TeamMemberStatus | undefined) ?? 'active'
                setForm((f) => ({ ...f, status: next === 'inactive' ? 'inactive' : 'active' }))
              }}
            />
          </div>

          {form.memberRole === 'operator' ? (
            <div>
              <label className="text-xs font-medium text-gray-600">Equipment</label>
              <MultiSelect
                className="mt-1"
                values={form.assignedEquipment}
                options={equipmentOptions}
                placeholder="Select equipment…"
                searchPlaceholder="Search equipment…"
                onChange={(values) => setForm((f) => ({ ...f, assignedEquipment: values }))}
              />
            </div>
          ) : null}
          </div>

          </div>
          <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 bg-white px-4 py-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
